import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Navbar from "../../../common/navbar";
import Breadcrumb from "./breadCrumb";
import MatchingCandidatesPage from "../matchingCandidates/matchingCandidatesPage";
import CandidateDetailPage from "../candidates/candidateDetailPage";
import Loader from "../../../common/loader";
import { RecruiterApi } from "../../../supports/recruiterApi";
import { generateRequestId } from "../../../common/common";
import unifiedAuthManager from "../../../services/unifiedAuthManager";

const SmartMatchPage = () => {
  const { mph_jobid } = useParams();
  const navigate = useNavigate();
  const user = unifiedAuthManager.getUnifiedUser();

  // Redux State
  const { smartMatchResults, smartMatchJobDetails } = useSelector(
    (state) => state.smartMatch,
  );

  const [results, setResults] = useState(smartMatchResults || []);
  const [jobDetails, setJobDetails] = useState(smartMatchJobDetails || null);
  const [loading, setLoading] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [candidateBreadcrumbName, setCandidateBreadcrumbName] = useState("");

  const smartMatchControllerRef = useRef(null);
  const [jobId, setJobId] = useState(jobDetails?.id || null);
  const [candidateActiveTab, setCandidateActiveTab] = useState("New");
  const [showMatchingFirstTime, setShowMatchingFirstTime] = useState(false);
  // Trigger fetch on mount and whenever jobId changes
  useEffect(() => {
    // If we don't have results or jobDetails, OR if the jobId in URL doesn't match the loaded job
    if (smartMatchResults.length === 0 && !smartMatchJobDetails) {
      fetchJobDetailsAndData();
    }
  }, [jobId]);

  useEffect(() => {
    return () => {
      if (smartMatchControllerRef.current) {
        smartMatchControllerRef.current.abort();
      }
    };
  }, []);

  const fetchJobDetailsAndData = async () => {
    try {
      setLoading(true);
      const response = await RecruiterApi.getJobDetails({
        job_id: mph_jobid,
        user_payload: { request_id: generateRequestId() },
        email: user.email,
      });

      if (response.success) {
        setJobDetails(response.data);
        setJobId(response.data.id);
        // Now fetch smart match data with the job details ID
        await fetchDataWithJobId(response.data.id);
        setLoading(false);
      } else {
        setLoading(false);
        setJobDetails(null);
      }
    } catch (error) {
      console.error("Error fetching job details:", error);
      setJobDetails(null);
    }
  };

  const fetchDataWithJobId = async (jobDetailsId) => {
    if (!jobDetailsId) return;

    if (smartMatchControllerRef.current) {
      smartMatchControllerRef.current.abort();
    }
    const controller = new AbortController();
    smartMatchControllerRef.current = controller;

    try {
      const response = await RecruiterApi.getSmartMatchCandidates({
        payload: {
          job_id: jobDetailsId,
        },
        user_payload: {
          request_id: generateRequestId(),
          email: user.email,
          tenant: "",
          user_id: user.id,
        },
      });

      if (response.success) {
        setResults(response.data.hits || []);
        setShowMatchingFirstTime(false);
        setCandidateActiveTab(null);
        setSelectedCandidate(null);
      } else {
        setResults([]);
      }
    } catch (error) {
      if (error.name === "AbortError" || error.message === "Aborted") {
        console.log("Fetch aborted");
      } else {
        console.error("Error fetching smart match candidates:", error);
      }
      setResults([]);
    } finally {
      smartMatchControllerRef.current = null;
    }
  };
  const handleCandidateSelect = (candidate, activeTab) => {
    try {
      setSelectedCandidate(candidate);
      setCandidateActiveTab(activeTab);
      if (candidate) {
        setCandidateBreadcrumbName( candidate?.full_name ?? `${candidate.first_name || ""} ${candidate.last_name || ""}`);
      } else {
        setCandidateBreadcrumbName("");
      }
    } catch (error) {
      console.log("Error in handleCandidateSelect:", error);
    }
  };

  const updateCandidate = (updatedCandidate) => {
    try {
      if (!updatedCandidate || !updatedCandidate.id) {
        return;
      }
      setResults((prev) =>
        prev.map((candidate) =>
          candidate.id === updatedCandidate.id ? updatedCandidate : candidate,
        ),
      );
    } catch (error) {
      console.error("Error updating candidate:", error);
    }
  };

  const handleUpdateAttribute = (updates) => {
    try {
        setResults(prevCandidates => 
    prevCandidates.map(candidate => 
      candidate.id === updates.id 
        ? { ...candidate, ...updates }
        : candidate
    )
  );
    } catch (error) {
      console.error("Error updating candidate attribute:", error);
    }

};

  const handleBreadcrumbClick = (path) => {
    if (path === "__show_matching_candidates__") {
      setSelectedCandidate(null);
      setShowMatchingFirstTime(true);
      return;
    }
    if (path) {
      setShowMatchingFirstTime(false);
      setSelectedCandidate(null);
      navigate(path);
    }
  };

  const breadcrumbs = [
    { name: "All Jobs", path: `/jobs` },
    {
      name: jobDetails?.title || "Job Details",
      path: `/jobs/jobDetails/${mph_jobid}`,
    },
    { name: "Matching Candidates", path: "__show_matching_candidates__" },
  ];

  if (selectedCandidate) {
    breadcrumbs.push({ name: candidateBreadcrumbName, path: null });
  }

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen main-background flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white relative main-background">
      <Navbar />
      <main className="w-full px-4 py-[6rem] overflow-hidden">
        <Breadcrumb items={breadcrumbs} onClick={handleBreadcrumbClick} />
        <div className="mx-auto px-3 pb-8">
          {/* 
             CONDITION: Display only if jobDetails exists and results are found.
             Added a "No Results" fallback for better UX.
          */}
          {jobDetails && results.length > 0 ? (
            selectedCandidate ? (
              <CandidateDetailPage
                candidate={selectedCandidate}
                similarCandidates={results}
                pageType="matching"
                jobId={jobDetails?.id}
                jobTitle={jobDetails?.title}
                onViewAll={() => {
                  setSelectedCandidate(null);
                  setShowMatchingFirstTime(true);
                  setCandidateBreadcrumbName("");
                }}
                onCandidateChange={handleCandidateSelect}
                jobMphId={jobDetails.mph_jobid}
                jobDetails={jobDetails}
                llmScore={selectedCandidate.llm_score}
                onUpdateCandidate={updateCandidate}
                onUpdateAttribute={handleUpdateAttribute }
              />
            ) : (
              <MatchingCandidatesPage
                jobDetails={jobDetails}
                matchingCandidates={results}
                onCandidateSelect={handleCandidateSelect}
                candidateActiveTab={candidateActiveTab}
                onUpdateCandidate={updateCandidate}
                isFirst={showMatchingFirstTime ? 1 : 0}
              />
            )
          ) : (
            !loading && (
              <div className="text-center py-20">
                <h3 className="text-base font-semibold">
                  No Matching Candidates Found
                </h3>
                <p className="text-gray-500">
                  We couldn't find any candidates matching this job description.
                </p>
              </div>
            )
          )}
        </div>
      </main>
    </div>
  );
};

export default SmartMatchPage;
